import { useNavigate } from "react-router-dom";
import { WebshopHeader } from "@/components/layout/WebshopHeader";
import { WebshopLayout } from "@/components/layout/WebshopLayout";
import { BodyPartPill } from "@/components/treatment-selection/BodyPartPill";
import { TherapyCard } from "@/components/treatment-selection/TherapyCard";
import { useCmsTherapyPlans } from "@/infra/hooks/useCmsTherapyPlans";
import { useIntl } from "@/infra/locale";
import { pushGtmEvent } from "@/infra/observability/googleTagManager";
import { AppRoutes } from "@/infra/router";
import { useTreatmentSelectionStore } from "@/infra/stores/treatmentSelectionStore";
import { webshopEnvironment } from "@akina/config";
import { Text } from "@akina/presentation";

// Priority at which we expect users to select each body part, to ensure the most common ones are shown first.
const BODY_PART_PRIORITY = ["back", "hip", "knee", "ankle"] as const;
const SKELETON_KEYS = ["s1", "s2", "s3", "s4", "s5"] as const;

export function TreatmentSelectionPage() {
  const { formatMessage } = useIntl();
  const { plans, universalPlan, bodyParts: rawBodyParts, bodyPartMap, isLoading } = useCmsTherapyPlans();

  const bodyParts = [
    ...BODY_PART_PRIORITY.filter(p => rawBodyParts.includes(p)),
    ...rawBodyParts.filter(p => !(BODY_PART_PRIORITY as readonly string[]).includes(p)),
  ];
  const websiteUrl = webshopEnvironment.websiteUrl;

  const selectedBodyPart = useTreatmentSelectionStore(s => s.selectedBodyPart);
  const selectedTemplateKey = useTreatmentSelectionStore(s => s.selectedTemplateKey);
  const { setBodyPart, selectTreatment } = useTreatmentSelectionStore(s => s.actions);

  const therapyPlans = [
    ...plans.filter(p => p.checkout_body_part_key === selectedBodyPart),
    ...(universalPlan ? [universalPlan] : []),
  ];

  const navigate = useNavigate();

  return (
    <WebshopLayout
      footer={{
        onContinue: () => navigate(AppRoutes.Result),
        onBack: () => globalThis.window.location.assign(websiteUrl),
        canContinue: !!selectedTemplateKey,
      }}>
      <div className="mx-auto flex w-full max-w-204 flex-col items-center gap-8">
        <WebshopHeader
          title={formatMessage({ id: "checkoutPage.selectTherapyPlanStep.title" })
            .split(/(?<=\.)\s*/)
            .map((part, i) => <span key={part}>{i > 0 && <br />}{part}</span>)}
          subtitle={formatMessage({ id: "checkoutPage.selectTherapyPlanStep.subtitle" })}
        />

        <div className="no-scrollbar flex w-full flex-nowrap gap-2 overflow-x-auto lg:flex-wrap lg:justify-center">
          {isLoading
            ? SKELETON_KEYS.map(key => (
                <BodyPartPill key={key} label="" selected={false} onClick={() => {}} isLoading />
              ))
            : bodyParts.map(bodyPart => (
                <BodyPartPill
                  key={bodyPart}
                  label={bodyPartMap[bodyPart]}
                  selected={selectedBodyPart === bodyPart}
                  onClick={() => {
                    pushGtmEvent("bodypart_selection_started");
                    setBodyPart(bodyPart);
                  }}
                />
              ))}
        </div>

        {selectedBodyPart ? (
          <div className="flex w-full flex-col gap-2 pt-2">
            <Text size="sm" color="text-gray-800/70">
              {selectedBodyPart === "other"
                ? formatMessage({ id: "checkoutPage.selectTherapyPlanStep.therapyPlanSectionDescriptionOther" })
                : formatMessage(
                    { id: "checkoutPage.selectTherapyPlanStep.therapyPlanSectionDescriptionDefault" },
                    { bodyPart: bodyPartMap[selectedBodyPart] ?? selectedBodyPart },
                  )}
            </Text>
            {therapyPlans.map(plan => (
              <TherapyCard
                key={plan.original_therapy_plan_key}
                name={plan.DisplayedName}
                description={plan.Description}
                selected={selectedTemplateKey === plan.original_therapy_plan_key}
                onClick={() => selectTreatment(plan.original_therapy_plan_key, plan.DisplayedName)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </WebshopLayout>
  );
}
